<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import { v4 as uuid } from 'uuid'
import type { HomePageConfig } from '../../configs/types'
import { theme } from '@/config/brands'
import { useToastStore } from '@/stores'
import { getDramaPlayerUrl } from '@/constants'
import { replaceDramaBanners, useDramaExperiment } from '@/utils/dramaExperiment'
import { dramaLocalEpisode, isVideoBridge, videoBridgeVideoUrl } from '@/utils/videoBridge'
import { useElementClick } from '@/composables/tracks'
import 'swiper/css'
import 'swiper/css/pagination'

const props = withDefaults(defineProps<{
  module: any
  config: HomePageConfig
  gotoBook: (id: string) => void
  /** 所在页签是否激活，驱动视频卡播放/暂停 */
  panelActive?: boolean
}>(), {
  panelActive: true,
})

const emit = defineEmits<{
  (e: 'update:bg-url', url: string): void
}>()

const { $toast } = useToastStore()

const { elementClick } = useElementClick()

const primaryColor = theme.primaryColor
const current = ref(0)
const swiper = ref<Swiper | null>(null)
const swiperRef = ref<HTMLElement | null>(null)
const paginationRef = ref<HTMLElement | null>(null)

const { selected } = useDramaExperiment()
const books = computed(() => {
  return replaceDramaBanners(props.module?.resourceList?.records || [], selected.value).slice(0, 10)
})

const currentBook = computed(() => {
  return books.value[current.value]
})

// 影游视频卡：命中视频桥接（play_mode=VIDEO_BRIDGE 且 video_entry 有效）时展示视频，否则展示图片
function isVideoItem(book: any) {
  return isVideoBridge(book)
}

function videoSrc(book: any) {
  // bannerVideoUrl 为运营单独配置的 banner 视频，优先；否则用桥接入口视频（oss_root + cover_oss_url）
  return book?.bannerVideoUrl || videoBridgeVideoUrl(book?.video_entry)
}

function isSlideActive(index: number) {
  return props.panelActive && index === current.value
}

/** 影游续看集数（仅用于 CTA 文案）：观看进度后端暂未下发，读播放器本地存档（与 Drama 页签、Library 页同口径），0 = 未开始 */
function dramaEpisode(book: any): number {
  if (!isVideoItem(book))
    return 0
  const ep = dramaLocalEpisode(book.contentId || book.id, book.video_entry)
  return book?.chapterNum ? Math.min(ep, book.chapterNum) : ep
}

// TODO: completed（Play Again）状态待接口下发
function ctaText(book: any) {
  const episode = dramaEpisode(book)
  if (episode > 0)
    return props.config.texts.continueEp(episode)
  if (book?.watchStatus === 'completed')
    return props.config.texts.playAgain
  return props.config.texts.play
}

// 横版封面卡尺寸（蓝湖 343x228，约 3:2）：slide 高度与封面组件占位比例共用同一份，
// 否则 BizBookCover 按默认竖版 133% 占位会把横图放大裁切
const BANNER_SLIDE_W = 343
const BANNER_SLIDE_H = 228
const slideStyle = { aspectRatio: `${BANNER_SLIDE_W} / ${BANNER_SLIDE_H}` }
const coverHeightRate = BANNER_SLIDE_H / BANNER_SLIDE_W * 100

// Banner 封面：优先横版封面 landscapeCoverUrl，缺省回退 contentCoverUrl
function bannerCover(book: any): string {
  return book?.landscapeCoverUrl || book?.contentCoverUrl || ''
}

// 更新背景图
function updateBgUrl(url: string) {
  emit('update:bg-url', url)
}

function initSwiper() {
  if (!swiperRef.value)
    return
  swiper.value = new Swiper(swiperRef.value, {
    modules: [Pagination, Autoplay],
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: 'auto', // slide 宽度由 CSS 控制（92%），配合 centeredSlides 居中
    spaceBetween: 12, // 图片之间的间距
    autoplay: {
      delay: 10000,
      disableOnInteraction: false,
    },
    pagination: {
      el: paginationRef.value,
      clickable: true,
    },
    // 视频卡需要真实 DOM 驱动播放，loop 克隆节点会失效，改用 rewind 回卷
    rewind: true,
    on: {
      init: (swiperInstance) => {
        current.value = swiperInstance.realIndex
        // 初始化时更新背景图
        const currentCover = bannerCover(books.value[swiperInstance.realIndex])
        if (currentCover)
          updateBgUrl(currentCover)
      },
      slideChange: async (swiperInstance) => {
        current.value = swiperInstance.realIndex
        // 切换时更新背景图
        const currentCover = bannerCover(books.value[swiperInstance.realIndex])
        if (currentCover)
          updateBgUrl(currentCover)
      },
      click(swiper: any, event) {
        // 声音开关按钮：只切静音，不触发跳转
        if ((event.target as Element)?.closest('.drama-mute-btn'))
          return
        const clickedSlide = (event.target as Element)?.closest('.swiper-slide')
        if (clickedSlide) {
          if (clickedSlide.classList.contains('swiper-slide-active'))
            handleGotoBook()
          else
            swiper.slideToClickedSlide()
        }
      },
    },
  })
}

function handleGotoBook() {
  const book = currentBook.value
  if (!book)
    return
  // 影游视频卡：直接整页跳转到独立播放器项目，不经详情页、无确认弹窗
  if (isVideoItem(book)) {
    elementClick('drama_banner')
    // 有进度也只落播放器 home 页（章节封面页），仅 Library 的 Continue 直达游戏
    try {
      window.location.href = getDramaPlayerUrl(book.id, 'store', false, book.video_entry)
    }
    catch { $toast('Unable to load this book. Please try again.') }
    return
  }
  if (book.id)
    props.gotoBook(book.id)
}

// 监听 module 变化，重新初始化 Swiper
watch(books, () => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
  // 重置当前索引
  current.value = 0
  nextTick(() => {
    initSwiper()
  })
})

onMounted(() => {
  initSwiper()
})

// 组件卸载时销毁 Swiper
onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
})
</script>

<template>
  <div ref="swiperRef" class="banner swiper">
    <div class="swiper-wrapper">
      <div v-for="(book, index) in books" :key="index" class="swiper-slide">
        <div class="slide-inner relative w-full" :style="slideStyle">
          <!-- 影游视频卡 -->
          <template v-if="isVideoItem(book)">
            <BizDramaVideo
              class="banner-video rounded-8px overflow-hidden"
              :src="videoSrc(book)"
              :poster="bannerCover(book)"
              :active="isSlideActive(index)"
              show-progress
              show-mute-button
            />
            <div class="video-overlay rounded-8px">
              <img src="/images/vowpath/home/drama-banner-badge.png" class="video-badge">
              <p class="video-title">
                {{ book.contentName }}
              </p>
              <div class="video-bottom">
                <button class="video-cta">
                  <i class="play-triangle" />
                  <span>{{ ctaText(book) }}</span>
                </button>
                <p class="video-desc">
                  {{ config.texts.dramaBannerDesc }}
                </p>
              </div>
            </div>
          </template>
          <!-- 小说图片卡 -->
          <template v-else>
            <BizBookCover
              :id="uuid()"
              :src="bannerCover(book)"
              :height-rate="coverHeightRate"
              :small-theater="!!book.small_theater"
              :extra="{
                module_name: module.showName,
                module_id: module.id,
                module_type: module.columnType,
                author: book.authorInfo?.author,
                ...book,
              }"
              class="book-cover rounded-8px overflow-hidden"
              :ignore-pictures-appear="true"
            />
            <!-- 底部渐变遮罩 + 书名 -->
            <div class="banner-overlay">
              <p class="banner-title truncate">
                {{ book.contentName }}
              </p>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div ref="paginationRef" class="swiper-pagination banner-dots" />
  </div>
</template>

<style scoped>
.banner {
  position: relative;
}

.swiper-slide {
  width: 92%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* slide 按容器宽度定高（aspect-ratio 由脚本注入，蓝湖 343x228），避免 vw 在桌面端导致错位 */
.book-cover,
.banner-video {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

/* 竖版封面顶对齐裁切，保持与横版封面一致的顶部视觉 */
.book-cover :deep(img) {
  object-position: center top;
}

/* 视频卡覆盖层（角标 / 静音 / 标题 / CTA / 副文案） */
.video-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(19, 22, 29, 0) 55%, rgba(19, 22, 29, 0.85) 100%);
}

.video-badge {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 130px;
  height: 20px;
}

.video-title {
  position: absolute;
  left: 12px;
  top: 40px;
  max-width: 75%;
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-weight: bold;
  font-size: 22px;
  line-height: 28px;
  color: #ffffff;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.video-bottom {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
}

.video-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  height: 30px;
  padding: 0 18px;
  border: none;
  border-radius: 15px;
  background: #ffffff;
  color: #17183b;
  font-size: 14px;
  font-weight: 600;
}

.play-triangle {
  width: 0;
  height: 0;
  margin-right: 5px;
  border-style: solid;
  border-width: 5px 0 5px 8px;
  border-color: transparent transparent transparent #17183b;
}

.video-desc {
  margin-top: 8px;
  max-width: 75%;
  font-size: 11px;
  line-height: 15px;
  color: rgba(255, 255, 255, 0.7);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

/* 底部渐变遮罩（对照蓝湖 rgba(19,22,29) 渐变） */
.banner-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 48px;
  border-radius: 0 0 8px 8px;
  background: linear-gradient(180deg, rgba(19, 22, 29, 0) 0%, rgba(19, 22, 29, 1) 100%);
  display: flex;
  align-items: flex-end;
  padding: 0 12px 10px;
}

.banner-title {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  max-width: 70%;
}

/* 非活跃slide样式 - 只降低透明度，不缩小 */
.swiper-slide:not(.swiper-slide-active) {
  opacity: 0.6;
}

/* 活跃slide样式 - 完整尺寸 */
.swiper-slide-active {
  opacity: 1;
  z-index: 2;
}

/* 分页器：叠加在 Banner 右下角 */
.banner-dots {
  position: absolute;
  left: auto;
  right: calc(4vw + 12px);
  bottom: 12px;
  width: auto;
  z-index: 3;
  display: flex;
  align-items: center;
}

/* 自定义分页器样式 */
:deep(.swiper-pagination-bullet) {
  width: 4px;
  height: 4px;
  background: #ffffff;
  border-radius: 2px;
  opacity: 0.5;
  margin: 0 4px;
}

:deep(.swiper-pagination-bullet-active) {
  width: 16px;
  height: 4px;
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
  border-radius: 3px;
  opacity: 1;
}
</style>
